<template>
  <v-container fluid style="max-width: 1200px;">

    <!-- E: 次の試験ヒーロー表示 -->
    <v-row v-if="nextExam" class="mb-4">
      <v-col cols="12">
        <v-card flat color="primary" dark class="pa-6">
          <v-row align="center">
            <v-col cols="12" sm="8">
              <div class="text-caption mb-1 white--text opacity-70">次の試験</div>
              <div class="text-h3 font-weight-bold mb-1">{{ nextExam.name }}</div>
              <div class="text-body-1">{{ nextExam.shikenbi }}</div>
            </v-col>
            <v-col cols="12" sm="4" class="text-center">
              <div class="text-h1 font-weight-bold">{{ nextExam.daysLeft }}</div>
              <div class="text-h4">日後</div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- スコアカード -->
    <v-row class="mb-4" v-if="totalScore > 0">
      <v-col cols="12">
        <v-card flat outlined class="pa-6">
          <v-row align="center">
            <v-col cols="12" sm="6" class="text-center text-sm-left">
              <div class="text-caption grey--text mb-1">あなたの資格スコア</div>
              <div class="d-flex align-center justify-center justify-sm-start">
                <span class="text-h1 font-weight-black primary--text">{{ totalScore }}</span>
                <span class="text-h4 primary--text ml-1">pt</span>
              </div>
              <div class="mt-2">
                <v-chip :color="levelColor" dark label>
                  {{ levelIcon }} {{ levelLabel }}
                </v-chip>
              </div>
              <div class="caption grey--text mt-2">難易度を設定した資格のみ集計</div>
            </v-col>
            <v-col cols="12" sm="6">
              <v-progress-linear
                v-for="d in difficultyBreakdown"
                :key="d.label"
                :value="d.pct"
                :color="d.color"
                height="20"
                rounded
                background-color="grey lighten-3"
                class="mb-2"
              >
                <template v-slot:default>
                  <span class="caption white--text font-weight-bold">
                    {{ d.label }}（{{ d.count }}件 / {{ d.pts }}pt）
                  </span>
                </template>
              </v-progress-linear>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- サマリーカード -->
    <v-row class="mb-4">
      <v-col cols="6" sm="3">
        <v-card flat outlined class="pa-4 text-center">
          <v-icon color="#276fdc" size="32" class="mb-2">mdi-badge-account-horizontal</v-icon>
          <div class="text-h2 font-weight-bold">{{ shikakuList.length }}</div>
          <div class="grey--text text-caption">取得済み</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card flat outlined class="pa-4 text-center">
          <v-icon color="secondary" size="32" class="mb-2">mdi-book-open-variant</v-icon>
          <div class="text-h2 font-weight-bold">{{ studyList.length }}</div>
          <div class="grey--text text-caption">勉強中</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card flat outlined class="pa-4 text-center">
          <v-icon color="warning" size="32" class="mb-2">mdi-star</v-icon>
          <div class="text-h2 font-weight-bold">{{ kininaruList.length }}</div>
          <div class="grey--text text-caption">気になる</div>
        </v-card>
      </v-col>
      <!-- C: 今月の取得数 -->
      <v-col cols="6" sm="3">
        <v-card flat outlined class="pa-4 text-center">
          <v-icon color="success" size="32" class="mb-2">mdi-calendar-today</v-icon>
          <div class="text-h2 font-weight-bold">{{ thisMonthCount }}</div>
          <div class="grey--text text-caption">今月の取得</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- C: 月平均取得数 -->
    <v-row class="mb-4" v-if="shikakuList.length > 0">
      <v-col cols="12">
        <v-card flat outlined class="pa-4">
          <v-row align="center">
            <v-col cols="12" sm="4" class="text-center">
              <div class="grey--text text-caption mb-1">年平均取得数</div>
              <div class="text-h2 font-weight-bold success--text">{{ yearlyAverage }}</div>
              <div class="grey--text text-caption">件 / 年</div>
            </v-col>
            <v-col cols="12" sm="4" class="text-center">
              <div class="grey--text text-caption mb-1">最近の取得</div>
              <template v-if="latestAcquired">
                <div class="text-subtitle-1 font-weight-bold text-truncate">{{ latestAcquired.name }}</div>
                <div class="grey--text text-caption">{{ latestAcquired.shutokubi }}</div>
              </template>
              <div v-else class="grey--text text-caption">—</div>
            </v-col>
            <v-col cols="12" sm="4" class="text-center">
              <div class="grey--text text-caption mb-1">資格取得歴</div>
              <div class="text-h2 font-weight-bold">{{ yearsOfHistory }}</div>
              <div class="grey--text text-caption">年</div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <!-- B: 期限切れ + 期限が近い資格 -->
      <v-col cols="12" md="6">
        <v-card flat outlined>
          <v-card-title class="text-h4 font-weight-bold">
            <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
            期限アラート
          </v-card-title>
          <v-divider />
          <v-card-text
            v-if="expiredList.length === 0 && nearDeadlineList.length === 0"
            class="grey--text text-center py-8"
          >
            期限に関する問題はありません
          </v-card-text>
          <v-list v-else dense>
            <!-- 期限切れ -->
            <template v-if="expiredList.length > 0">
              <v-subheader class="error--text font-weight-bold">期限切れ</v-subheader>
              <v-list-item v-for="item in expiredList" :key="'exp-' + item.id + item.type">
                <v-list-item-icon>
                  <v-icon color="error">mdi-alert</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold error--text">{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.type }} : {{ item.date }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip small color="error" dark>{{ Math.abs(item.daysLeft) }}日超過</v-chip>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="nearDeadlineList.length > 0" />
            </template>
            <!-- 期限が近い -->
            <template v-if="nearDeadlineList.length > 0">
              <v-subheader class="warning--text font-weight-bold">期限が近い（90日以内）</v-subheader>
              <v-list-item v-for="item in nearDeadlineList" :key="'near-' + item.id + item.type">
                <v-list-item-icon>
                  <v-icon :color="item.daysLeft <= 30 ? 'error' : 'warning'">mdi-clock-alert-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.type }} : {{ item.date }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip small :color="item.daysLeft <= 30 ? 'error' : 'warning'" dark>
                    あと{{ item.daysLeft }}日
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>

      <!-- 直近の試験一覧 -->
      <v-col cols="12" md="6">
        <v-card flat outlined>
          <v-card-title class="text-h4 font-weight-bold">
            <v-icon color="primary" class="mr-2">mdi-calendar-check</v-icon>
            直近の試験
          </v-card-title>
          <v-divider />
          <v-card-text v-if="upcomingExamList.length === 0" class="grey--text text-center py-8">
            予定されている試験はありません
          </v-card-text>
          <v-list v-else dense>
            <v-list-item v-for="item in upcomingExamList" :key="item.name">
              <v-list-item-icon>
                <v-icon color="primary">mdi-pencil-circle-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>試験日 : {{ item.shikenbi }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-chip small color="primary" dark>あと{{ item.daysLeft }}日</v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <!-- タグ別資格数 -->
    <v-row class="mt-4" v-if="tagStats.length > 0">
      <v-col cols="12">
        <v-card flat outlined class="pa-4">
          <v-card-title class="text-h4 font-weight-bold mb-2">
            <v-icon color="primary" class="mr-2">mdi-tag-multiple-outline</v-icon>
            タグ別資格数
          </v-card-title>
          <v-divider class="mb-4" />
          <div
            v-for="tag in tagStats"
            :key="tag.name"
            class="mb-3"
          >
            <div class="d-flex justify-space-between align-center mb-1">
              <span class="body-2 font-weight-bold">{{ tag.name }}</span>
              <span class="body-2 grey--text">{{ tag.total }}件</span>
            </div>
            <v-progress-linear
              :value="(tag.total / tagStats[0].total) * 100"
              height="18"
              rounded
              background-color="grey lighten-3"
              color="primary"
            >
              <template v-slot:default>
                <div class="d-flex" style="width:100%; height:100%;">
                  <div
                    v-if="tag.shikaku > 0"
                    :style="{ width: (tag.shikaku / tag.total * 100) + '%', background: '#276fdc' }"
                    :title="`取得済み: ${tag.shikaku}件`"
                  />
                  <div
                    v-if="tag.study > 0"
                    :style="{ width: (tag.study / tag.total * 100) + '%', background: '#9c27b0' }"
                    :title="`勉強中: ${tag.study}件`"
                  />
                  <div
                    v-if="tag.kininaru > 0"
                    :style="{ width: (tag.kininaru / tag.total * 100) + '%', background: '#ff9800' }"
                    :title="`気になる: ${tag.kininaru}件`"
                  />
                </div>
              </template>
            </v-progress-linear>
            <div class="d-flex mt-1" style="gap: 12px;">
              <span v-if="tag.shikaku > 0" class="caption" style="color:#276fdc">
                <v-icon x-small style="color:#276fdc">mdi-badge-account-horizontal</v-icon>
                取得済み {{ tag.shikaku }}
              </span>
              <span v-if="tag.study > 0" class="caption" style="color:#9c27b0">
                <v-icon x-small style="color:#9c27b0">mdi-book-open-variant</v-icon>
                勉強中 {{ tag.study }}
              </span>
              <span v-if="tag.kininaru > 0" class="caption" style="color:#ff9800">
                <v-icon x-small style="color:#ff9800">mdi-star</v-icon>
                気になる {{ tag.kininaru }}
              </span>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- 年間取得数グラフ -->
    <v-row class="mt-4" v-if="yearlyChartData.labels.length > 0">
      <v-col cols="12">
        <v-card flat outlined class="pa-4">
          <v-card-title class="text-h4 font-weight-bold mb-4">
            <v-icon color="success" class="mr-2">mdi-chart-bar</v-icon>
            年間取得数
          </v-card-title>
          <v-divider class="mb-4" />
          <chartist
            type="Bar"
            :data="yearlyChartData"
            :options="barChartOptions"
            style="height: 200px;"
          />
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import { mapState } from 'vuex'

  export default {
    name: 'Dashboard',
    computed: {
      ...mapState({
        shikakuList: state => state.user.shikakuList,
        studyList: state => state.user.studyList,
        kininaruList: state => state.user.kininaruList,
      }),
      // E: 一番近い試験
      nextExam () {
        const today = new Date()
        const upcoming = this.studyList
          .filter(item => item.shikenbi)
          .map(item => ({
            ...item,
            daysLeft: Math.ceil((new Date(item.shikenbi) - today) / (1000 * 60 * 60 * 24)),
          }))
          .filter(item => item.daysLeft >= 0)
          .sort((a, b) => a.daysLeft - b.daysLeft)
        return upcoming.length > 0 ? upcoming[0] : null
      },
      // C: 今月の取得数
      thisMonthCount () {
        const now = new Date()
        const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
        return this.shikakuList.filter(item => item.shutokubi && item.shutokubi.startsWith(ym)).length
      },
      // C: 年平均取得数
      yearlyAverage () {
        if (this.shikakuList.length === 0) return 0
        const withDate = this.shikakuList.filter(item => item.shutokubi)
        if (withDate.length === 0) return 0
        const oldest = withDate.reduce((min, item) => item.shutokubi < min ? item.shutokubi : min, withDate[0].shutokubi)
        const years = Math.max(1, (new Date() - new Date(oldest)) / (1000 * 60 * 60 * 24 * 365))
        return (this.shikakuList.length / years).toFixed(1)
      },
      // C: 最近の取得
      latestAcquired () {
        const withDate = this.shikakuList.filter(item => item.shutokubi)
        if (withDate.length === 0) return null
        return withDate.reduce((latest, item) => item.shutokubi > latest.shutokubi ? item : latest, withDate[0])
      },
      // C: 資格取得歴（年数）
      yearsOfHistory () {
        const withDate = this.shikakuList.filter(item => item.shutokubi)
        if (withDate.length === 0) return 0
        const oldest = withDate.reduce((min, item) => item.shutokubi < min ? item.shutokubi : min, withDate[0].shutokubi)
        return Math.max(1, Math.floor((new Date() - new Date(oldest)) / (1000 * 60 * 60 * 24 * 365)))
      },
      // B: 期限切れ
      expiredList () {
        const today = new Date()
        const result = []
        for (const item of this.shikakuList) {
          for (const { key, label } of [
            { key: 'yuukoukigen', label: '有効期限' },
            { key: 'koushinkigen', label: '更新期限' },
          ]) {
            if (!item[key]) continue
            const diff = Math.ceil((new Date(item[key]) - today) / (1000 * 60 * 60 * 24))
            if (diff < 0) {
              result.push({ id: item.id, name: item.name, type: label, date: item[key], daysLeft: diff })
            }
          }
        }
        return result.sort((a, b) => a.daysLeft - b.daysLeft)
      },
      // B: 期限が近い（90日以内）
      nearDeadlineList () {
        const today = new Date()
        const result = []
        for (const item of this.shikakuList) {
          for (const { key, label } of [
            { key: 'yuukoukigen', label: '有効期限' },
            { key: 'koushinkigen', label: '更新期限' },
          ]) {
            if (!item[key]) continue
            const diff = Math.ceil((new Date(item[key]) - today) / (1000 * 60 * 60 * 24))
            if (diff >= 0 && diff <= 90) {
              result.push({ id: item.id, name: item.name, type: label, date: item[key], daysLeft: diff })
            }
          }
        }
        return result.sort((a, b) => a.daysLeft - b.daysLeft)
      },
      // 直近の試験一覧
      upcomingExamList () {
        const today = new Date()
        return this.studyList
          .filter(item => item.shikenbi)
          .map(item => ({
            ...item,
            daysLeft: Math.ceil((new Date(item.shikenbi) - today) / (1000 * 60 * 60 * 24)),
          }))
          .filter(item => item.daysLeft >= 0)
          .sort((a, b) => a.daysLeft - b.daysLeft)
          .slice(0, 5)
      },
      // 資格スコア
      totalScore () {
        const pts = { S: 100, A: 70, B: 40, C: 20 }
        return this.shikakuList.reduce((sum, item) => sum + (pts[item.difficulty] || 0), 0)
      },
      levelLabel () {
        if (this.totalScore >= 1000) return 'マスター'
        if (this.totalScore >= 600) return 'エキスパート'
        if (this.totalScore >= 300) return 'スペシャリスト'
        if (this.totalScore >= 100) return 'エクスプローラー'
        return 'ビギナー'
      },
      levelIcon () {
        if (this.totalScore >= 1000) return '💎'
        if (this.totalScore >= 600) return '🔥'
        if (this.totalScore >= 300) return '⚡'
        if (this.totalScore >= 100) return '📘'
        return '🌱'
      },
      levelColor () {
        if (this.totalScore >= 1000) return 'cyan darken-1'
        if (this.totalScore >= 600) return 'deep-orange'
        if (this.totalScore >= 300) return 'purple'
        if (this.totalScore >= 100) return 'blue'
        return 'green'
      },
      difficultyBreakdown () {
        const defs = [
          { key: 'S', label: 'S 最難関', color: 'deep-orange', pts: 100 },
          { key: 'A', label: 'A 難関',   color: 'purple',      pts: 70 },
          { key: 'B', label: 'B 標準',   color: 'blue',        pts: 40 },
          { key: 'C', label: 'C 入門',   color: 'green',       pts: 20 },
        ]
        const max = this.totalScore || 1
        return defs.map(d => {
          const items = this.shikakuList.filter(i => i.difficulty === d.key)
          const count = items.length
          const pts = count * d.pts
          return { ...d, count, pts, pct: Math.round((pts / max) * 100) }
        }).filter(d => d.count > 0)
      },
      // タグ別資格数
      tagStats () {
        const map = {}
        const add = (list, key) => {
          for (const item of list) {
            if (!item.tags || !Array.isArray(item.tags)) continue
            for (const tag of item.tags) {
              if (!map[tag]) map[tag] = { name: tag, shikaku: 0, study: 0, kininaru: 0, total: 0 }
              map[tag][key]++
              map[tag].total++
            }
          }
        }
        add(this.shikakuList, 'shikaku')
        add(this.studyList, 'study')
        add(this.kininaruList, 'kininaru')
        return Object.values(map).sort((a, b) => b.total - a.total)
      },
      // 年間取得数グラフ（過去5年）
      yearlyChartData () {
        const currentYear = new Date().getFullYear()
        const years = []
        const counts = []
        for (let y = currentYear - 4; y <= currentYear; y++) {
          years.push(`${y}年`)
          counts.push(this.shikakuList.filter(item => item.shutokubi && item.shutokubi.startsWith(String(y))).length)
        }
        if (counts.every(c => c === 0)) return { labels: [], series: [[]] }
        return { labels: years, series: [counts] }
      },
      barChartOptions () {
        return {
          axisY: { onlyInteger: true, low: 0 },
          chartPadding: { top: 20, right: 20, bottom: 0, left: 20 },
        }
      },
    },
  }
</script>

<style scoped>
  ::v-deep .ct-bar {
    stroke: #4CAF50;
    stroke-width: 30px;
  }
  ::v-deep .ct-label {
    font-size: 0.85rem;
  }
</style>
